import { useEffect, useState, type FormEvent } from "react";
import { Field, Input } from "@/components/base/FormControls";
import DocumentIdentityCard from "@/components/feature/DocumentIdentityCard";
import { describeSupabaseError } from "@/store/shared/dataErrors";
import { useParametres } from "../ParametresProvider";
import type { AgenceProfil } from "../types";

export default function ProfilSection() {
  const { profil, updateProfil, peutEditer } = useParametres();
  const [form, setForm] = useState<AgenceProfil>(profil);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reflète le profil réel (chargé depuis la base) dans le formulaire.
  useEffect(() => {
    setForm(profil);
  }, [profil]);

  const update = (key: keyof AgenceProfil, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setSaveError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!peutEditer) return;
    const nextErrors: Record<string, string> = {};
    if (!form.nomAgence.trim()) nextErrors.nomAgence = "Le nom de l'agence est obligatoire.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      await updateProfil({
        nomAgence: form.nomAgence.trim(),
        responsable: form.responsable.trim(),
        email: form.email,
        telephone: form.telephone.trim(),
        logo: form.logo,
        logoPath: form.logoPath,
        adresse: form.adresse.trim(),
        siteWeb: form.siteWeb.trim(),
        ifu: form.ifu.trim(),
        rccm: form.rccm.trim(),
      });
      setSaved(true);
    } catch (caught) {
      setSaveError(describeSupabaseError(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
          <i className="ri-building-line text-base" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Profil de l'agence</h3>
          <p className="text-xs text-foreground-500">Informations générales de votre agence et de son responsable.</p>
        </div>
      </header>

      {!peutEditer && (
        <p className="mx-4 mt-4 flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs text-foreground-600 md:mx-5">
          <i className="ri-lock-2-line mt-0.5 shrink-0 text-sm text-foreground-500" aria-hidden="true" />
          Seul le directeur d'agence peut modifier le profil de l'agence.
        </p>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 p-4 md:p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nom de l'agence" htmlFor="param-nom" required error={errors.nomAgence} className="sm:col-span-2">
            <Input
              id="param-nom"
              value={form.nomAgence}
              onChange={(event) => update("nomAgence", event.target.value)}
              placeholder="Ex. Immo Gestion Burkina"
              maxLength={120}
              invalid={Boolean(errors.nomAgence)}
              disabled={!peutEditer}
            />
          </Field>
          <Field label="Responsable" htmlFor="param-responsable">
            <Input
              id="param-responsable"
              value={form.responsable}
              onChange={(event) => update("responsable", event.target.value)}
              placeholder="Nom du responsable"
              icon="ri-user-star-line"
              maxLength={80}
              disabled={!peutEditer}
            />
          </Field>
          <Field label="Téléphone" htmlFor="param-telephone" hint="Format local, ex. 70 00 00 00.">
            <Input
              id="param-telephone"
              type="tel"
              value={form.telephone}
              onChange={(event) => update("telephone", event.target.value)}
              placeholder="70 00 00 00"
              icon="ri-phone-line"
              maxLength={40}
              disabled={!peutEditer}
            />
          </Field>
          <Field
            label="Email de l'agence"
            htmlFor="param-email"
            hint="Adresse de connexion — contactez le support pour la modifier."
            className="sm:col-span-2"
          >
            <Input id="param-email" type="email" value={form.email} icon="ri-mail-line" maxLength={120} readOnly disabled />
          </Field>
          <Field
            label="Adresse"
            htmlFor="param-adresse"
            hint="Affichée dans l'en-tête et le pied de page des documents PDF."
            className="sm:col-span-2"
          >
            <Input
              id="param-adresse"
              value={form.adresse}
              onChange={(event) => update("adresse", event.target.value)}
              placeholder="Ex. Avenue Kwame N'Krumah, Ouagadougou"
              icon="ri-map-pin-line"
              maxLength={160}
              disabled={!peutEditer}
            />
          </Field>
        </div>

        <div className="border-t border-background-200 pt-5">
          <DocumentIdentityCard
            logo={form.logo}
            onLogoChange={(value) => {
              setForm((prev) => ({ ...prev, logo: value }));
              setSaved(false);
              setSaveError(null);
            }}
            nom={form.nomAgence}
            adresse={form.adresse}
            telephone={form.telephone}
            email={form.email}
            siteWeb={form.siteWeb}
            onSiteWebChange={(value) => update("siteWeb", value)}
            ifu={form.ifu}
            onIfuChange={(value) => update("ifu", value)}
            rccm={form.rccm}
            onRccmChange={(value) => update("rccm", value)}
            logoLabel="Logo de l'agence"
            disabled={!peutEditer}
          />
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          {saveError ? (
            <p className="flex items-center gap-1.5 text-xs text-primary-700">
              <i className="ri-error-warning-line text-sm" aria-hidden="true" />
              L'enregistrement a échoué. Réessayez.
            </p>
          ) : saved ? (
            <p className="flex items-center gap-1.5 text-xs text-secondary-700">
              <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
              Profil de l'agence enregistré.
            </p>
          ) : (
            <span className="text-xs text-foreground-400">
              {peutEditer ? "Vos informations sont enregistrées dans votre compte." : "Lecture seule."}
            </span>
          )}
          <button
            type="submit"
            disabled={saving || !peutEditer}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className={saving ? "ri-loader-4-line animate-spin text-base" : "ri-save-line text-base"} aria-hidden="true" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </section>
  );
}